import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config';
import { MAX_ATTACHMENTS, validateFileSelection } from '../utils/fileUploadValidation';
import ToastMessage from './ToastMessage';
import useToast from '../hooks/useToast';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
import {
  addLocalDevNotification,
  getLocalDevSubtasks,
  isLocalDevCompanyId,
  updateLocalDevSubtask,
} from '../utils/localDevAuth';

const TaskCalendar = ({ user }) => {
  const { t, locale } = useLanguage();
  const weekDays = [
    t('calendar.sun'),
    t('calendar.mon'),
    t('calendar.tue'),
    t('calendar.wed'),
    t('calendar.thu'),
    t('calendar.fri'),
    t('calendar.sat'),
  ];
  const formatStatus = (status) => {
    if (status === 'pending_approval') return t('teamPerformance.pendingApproval');
    return t(`task.${status}`) || status || t('common.na');
  };
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allTasks, setAllTasks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    submitted_files: [],
    submitted_links: [],
  });
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { toast, showToast, clearToast } = useToast();
  const navigate = useNavigate();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = user?.company?.id || localStorage.getItem('company_id') || '1';

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const localTasks = getLocalDevSubtasks()
          .filter(task => task.major_task?.project?.company?.id === user.company?.id)
          .filter(task => (
            user.role === 'team_member'
              ? task.assigned_to?.id === user.id
              : task.assigned_by?.id === user.id || task.assigned_to?.id === user.id
          ));
        setAllTasks(localTasks);
        setError('');
        return;
      }
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      let tasksRes;
      const config = { headers: getAuthHeaders(token) };
      if (user.role === 'team_member') {
        tasksRes = await axios.get(`${API_URL}/api/subtasks/`, config);
        const filteredTasks = tasksRes.data.filter(task => task.assigned_to?.id === user.id);
        const tasksWithDetails = await Promise.all(filteredTasks.map(async (task) => {
          try {
            const majorTaskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, config);
            return { ...task, major_task: majorTaskRes.data };
          } catch (error) {
            console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
            return task;
          }
        }));
        setAllTasks(tasksWithDetails);
      } else if (user.role === 'manager') {
        tasksRes = await axios.get(`${API_URL}/api/subtasks/`, config);
        const filteredTasks = tasksRes.data.filter(task => task.assigned_by?.id === user.id);
        const tasksWithDetails = await Promise.all(filteredTasks.map(async (task) => {
          try {
            const majorTaskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, config);
            return { ...task, major_task: majorTaskRes.data };
          } catch (error) {
            console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
            return task;
          }
        }));
        setAllTasks(tasksWithDetails);
      } else {
        setAllTasks([]);
      }
      setError('');
    } catch (err) {
      console.error('Error fetching tasks:', err);
      if (err.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(err, {
        fallback: t('myTasks.failedLoad'),
      }));
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyId, isLocalDevSession, navigate, t, user]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    const selectedDateStr = formatDate(selectedDate);
    const filtered = allTasks.filter(task => {
      if (!task.due_date) return false;
      const taskDateStr = formatDate(new Date(task.due_date));
      return taskDateStr === selectedDateStr;
    });
    setTasks(filtered);
  }, [allTasks, selectedDate]);

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const startDay = startOfMonth.getDay();

  const days = [];
  for (let i = 0; i < startDay; i += 1) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const getDateKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  const tasksCountByDate = allTasks.reduce((counts, task) => {
    if (!task.due_date) return counts;
    const key = getDateKey(new Date(task.due_date));
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const openSubmissionModal = (taskId) => {
    setSelectedTaskId(taskId);
    setSubmissionData({ submitted_files: [], submitted_links: [] });
    setUploadSuccess(false);
    setSubmissionModalOpen(true);
  };

  const handleSubmissionInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'submitted_files') {
      const incomingFiles = Array.from(files || []);
      setSubmissionData(prev => {
        const validation = validateFileSelection(prev.submitted_files, incomingFiles);
        if (!validation.isValid) {
          showToast(validation.error, 'error');
          return prev;
        }
        setError('');
        return { ...prev, submitted_files: validation.files };
      });
      e.target.value = '';
      return;
    }
    setSubmissionData(prev => ({ ...prev, [name]: value }));
  };

  const removeSubmissionFile = (index) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_files: prev.submitted_files.filter((_, i) => i !== index),
    }));
  };

  const addSubmissionLink = () => {
    setSubmissionData(prev => {
      if (prev.submitted_links.length >= MAX_ATTACHMENTS) {
        setError(t('toast.maxLinksAllowed'));
        return prev;
      }
        setError('');
      return { ...prev, submitted_links: [...prev.submitted_links, ''] };
    });
  };

  const updateSubmissionLink = (index, value) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_links: prev.submitted_links.map((link, i) => (i === index ? value : link)),
    }));
  };

  const removeSubmissionLink = (index) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_links: prev.submitted_links.filter((_, i) => i !== index),
    }));
  };

  const handleSendForApproval = async () => {
    if (!selectedTaskId) return;
    try {
      setUploading(true);
      if (isLocalDevSession) {
        const updatedTask = updateLocalDevSubtask(selectedTaskId, {
          status: 'pending_approval',
          submitted_links: submissionData.submitted_links.filter(link => link && link.trim()),
        });
        if (updatedTask?.assigned_by?.email) {
          addLocalDevNotification({
            recipientEmail: updatedTask.assigned_by.email,
            type: 'task_pending_approval',
            message: `${user.name || user.email} has submitted the task '${updatedTask.name}' for your approval.`,
            messageHi: `कार्य '${updatedTask.name}' स्वीकृति के लिए जमा किया गया है।`,
            createdBy: user.email,
          });
        }
        setSuccess(t('toast.taskSentApproval'));
        setUploadSuccess(true);
        setError('');
        setSubmissionModalOpen(false);
        window.dispatchEvent(new Event('notifications:refresh'));
        fetchTasks();
        setUploading(false);
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const formData = new FormData();
      formData.append('status', 'pending_approval');
      if (submissionData.submitted_files && submissionData.submitted_files.length > 0) {
        submissionData.submitted_files.forEach(file => {
          formData.append('submitted_files', file);
        });
      }
      if (submissionData.submitted_links && submissionData.submitted_links.length > 0) {
        submissionData.submitted_links.forEach(link => {
          if (link) {
            formData.append('submitted_links', link);
          }
        });
      }

      await axios.patch(
        `${API_URL}/api/subtasks/${selectedTaskId}/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSuccess(t('toast.taskSentApproval'));
      setUploadSuccess(true);
      setError('');
      setSubmissionModalOpen(false);
      window.dispatchEvent(new Event('notifications:refresh'));
      fetchTasks();
    } catch (err) {
      console.error('Error sending for approval:', err);
      if (err.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(err, {
        fallback: t('toast.failedSendApproval'),
      }));
      setSuccess('');
    }
    setUploading(false);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      if (isLocalDevSession) {
        updateLocalDevSubtask(taskId, { status: newStatus });
        setSuccess(t('toast.taskUpdatedSuccess'));
        setError('');
        fetchTasks();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const formData = new FormData();
      formData.append('status', newStatus);

      await axios.patch(
        `${API_URL}/api/subtasks/${taskId}/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSuccess(t('toast.taskUpdatedSuccess'));
      setError('');
      fetchTasks();
    } catch (err) {
      console.error('Error updating status:', err);
      if (err.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(err, {
        fallback: t('toast.failedUpdateTaskStatus'),
      }));
      setSuccess('');
    }
  };

  return (
    <div>
      <ToastMessage toast={toast} onClose={clearToast} />
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">{t('calendar.title', { name: user.code || (user.role === 'manager' ? t('auth.manager') : t('auth.teamMember')) })}</h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 rounded-lg hover:bg-hover-bg transition"
            >
              <FaChevronLeft />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-text">
              {currentMonth.toLocaleString(locale, { month: 'long', year: 'numeric' })}
            </h2>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 rounded-lg hover:bg-hover-bg transition"
            >
              <FaChevronRight />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-2 text-xs sm:text-sm text-secondary-text mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center font-medium">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2 text-sm sm:text-base">
            {days.map((date, index) => {
              const hasTasks = Boolean(date && tasksCountByDate[getDateKey(date)]);
              return (
                <button
                  key={`${date ? date.toISOString() : 'empty'}-${index}`}
                  type="button"
                  onClick={() => date && setSelectedDate(date)}
                  className={`relative h-10 sm:h-12 rounded-lg flex items-center justify-center border transition
                    ${date ? 'border-secondary hover:bg-hover-bg' : 'border-transparent cursor-default'}
                    ${isSameDay(date, selectedDate)
                      ? 'bg-primary text-white border-primary'
                      : hasTasks
                        ? 'bg-green-100 border-green-300 text-green-900'
                        : 'text-text'}`}
                  disabled={!date}
                >
                  {date ? date.getDate() : ''}
                  {hasTasks && !isSameDay(date, selectedDate) && (
                    <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-green-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="card p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-secondary-text mb-2 sm:mb-3">
            {t('calendar.tasksOnDate', { date: formatDate(selectedDate) })}
          </h2>
          {loading ? (
            <p className="text-text italic text-sm sm:text-base">{t('common.loading')}</p>
          ) : tasks.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">{t('calendar.noTasksForDate')}</p>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => (
                <div key={task.id} className="border border-secondary rounded-lg p-3 sm:p-4">
                  <p className="text-text font-semibold text-sm sm:text-base">{task.name}</p>
                  <p className="text-text text-xs sm:text-sm">{t('common.project')}: {task.major_task?.project?.name || t('common.na')}</p>
                  <p className="text-text text-xs sm:text-sm">{t('dashboard.majorTask')}: {task.major_task?.name || t('common.na')}</p>
                  <p className="text-text text-xs sm:text-sm">{t('common.status')}: {formatStatus(task.status)}</p>
                  <p className="text-text text-xs sm:text-sm">
                    {t('common.dueDateTime')}: {task.due_date ? formatDateTime24(task.due_date, locale) : t('common.na')}
                  </p>
                  <div className="mt-3">
                    {user.role === 'team_member' ? (
                      <select
                        className="input-field p-2 text-text text-sm sm:text-base w-full"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value === 'submit') {
                            openSubmissionModal(task.id);
                          }
                          e.target.value = '';
                        }}
                        disabled={task.status !== 'ongoing'}
                      >
                        <option value="">{t('calendar.selectAction')}</option>
                        <option value="submit">{t('calendar.sendForApproval')}</option>
                      </select>
                    ) : (
                      <select
                        className="input-field p-2 text-text text-sm sm:text-base w-full"
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value)}
                      >
                        <option value="ongoing">{t('task.ongoing')}</option>
                        <option value="completed">{t('task.completed')}</option>
                        <option value="pending_approval">{t('teamPerformance.pendingApproval')}</option>
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {submissionModalOpen && (
        <div className="overlay-backdrop fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="dialog-surface w-full max-w-md p-6">
            <h2 className="mb-4 text-xl font-semibold text-text">{t('calendar.submitTask')}</h2>
            <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-text">{t('createTask.attachFilesOptional')}</label>
              <input
                type="file"
                name="submitted_files"
                multiple
                onChange={handleSubmissionInputChange}
                className="input-field w-full p-2 text-sm"
              />
              <p className="mt-1 text-xs text-secondary-text">{t('createTask.maxFilesHelp')}</p>
              {submissionData.submitted_files.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-text">
                  {submissionData.submitted_files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex items-center justify-between">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeSubmissionFile(index)}
                        className="text-xs text-error transition hover:opacity-80"
                      >
                        {t('common.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-text">{t('createTask.attachLinksOptional')}</label>
                <button
                  type="button"
                  onClick={addSubmissionLink}
                  className="text-xs text-accent-blue transition hover:opacity-80"
                >
                  {t('common.addLink')}
                </button>
              </div>
              <p className="mb-2 text-xs text-secondary-text">{t('createTask.maxLinksHelp')}</p>
              {submissionData.submitted_links.length === 0 && (
                <p className="text-xs italic text-secondary-text">{t('createTask.noLinksAdded')}</p>
              )}
              <div className="space-y-2">
                {submissionData.submitted_links.map((link, index) => (
                  <div key={`submitted-link-${index}`} className="flex items-center space-x-2">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateSubmissionLink(index, e.target.value)}
                      placeholder="https://example.com"
                      className="input-field w-full p-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeSubmissionLink(index)}
                      className="text-xs text-error transition hover:opacity-80"
                    >
                        {t('common.remove')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setSubmissionModalOpen(false)}
                className="secondary-btn"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSendForApproval}
                className="primary-btn flex items-center"
                disabled={uploading}
              >
                {uploading && (
                  <span className="mr-2 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {!uploading && uploadSuccess && <FaCheck className="mr-2" />}
                {uploading ? t('common.uploading') : t('common.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCalendar;
